import * as RD from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { ReactNode } from "react";

/** Accessible modal (focus trap, Escape to close, labelled title) built on Radix. */
export function Dialog({
  open, onOpenChange, title, description, children, footer, size = "md",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
}) {
  const width = size === "sm" ? "max-w-sm" : size === "lg" ? "max-w-2xl" : "max-w-lg";
  return (
    <RD.Root open={open} onOpenChange={onOpenChange}>
      <RD.Portal>
        <RD.Overlay className="fixed inset-0 z-40 bg-slate-900/25 backdrop-blur-md" />
        <RD.Content
          className={`glass text-default fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[calc(100vw-2rem)] ${width} -translate-x-1/2 -translate-y-1/2 overflow-y-auto scroll-soft rounded-2xl p-6 shadow-2xl focus:outline-none`}
          {...(description ? {} : { "aria-describedby": undefined })}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <RD.Title className="text-lg font-semibold tracking-tight">{title}</RD.Title>
              {description && <RD.Description className="mt-1 text-sm text-muted">{description}</RD.Description>}
            </div>
            <RD.Close className="focus-ring rounded-lg p-1.5 text-subtle hover:bg-white/70 hover:text-default" aria-label="Close">
              <X className="h-4 w-4" />
            </RD.Close>
          </div>
          {children}
          {footer && <div className="mt-5 flex flex-wrap justify-end gap-2">{footer}</div>}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
